import { PrebidModuleData, PrebidModuleDataBySite, PrebidModuleType } from '@pubstack/common/src/prebidModule'
import { FunctionComponent, useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useAnalyticsModule, useIdentityModule, usePrebidModulesConfig, useRealTimeDataModule } from '~/api/adm-api.hook'
import { putRenderingModule } from '~/api/adm/rendering-module.api'
import { useCatalogModules, usePrebidModulesCatalog } from '~/api/catalog.api'
import { useToast } from '~/components/Toast/useToasts'
import { PureModulesBySite } from './PureModulesBySite'
import { AdmSitePageContext } from '~/modules/pbs/sites/site/useSitePageContext'

export const ModulesBySite: FunctionComponent = () => {
  const [modules, setModules] = useState<PrebidModuleDataBySite<PrebidModuleType>[]>([])
  const { site } = useOutletContext<AdmSitePageContext>()
  const { prebidModulesConfig } = usePrebidModulesConfig(null)
  const { byId: rtdModuleUpdate } = useRealTimeDataModule(null)
  const { byId: idModuleUpdate } = useIdentityModule(null)
  const { byId: analyticsModuleUpdate } = useAnalyticsModule(null)
  const { data: catalogData, isLoading: isCatalogLoading } = useCatalogModules()
  const { modules: prebidModulesCatalog, isLoading: isModuleCatalogLoading } = usePrebidModulesCatalog()
  const toast = useToast()

  const getModulesData = async () => {
    const siteModulesConfig = await prebidModulesConfig.get(site.id || '')
    const defaultData: PrebidModuleDataBySite<PrebidModuleType>[] = catalogData
      .filter((m) => !siteModulesConfig.some((s) => s.config.name === m.code))
      .map((m) => ({
        code: m.code,
        moduleData: prebidModulesCatalog.find((module) => module.code === m.code)?.defaultValues,
        siteId: site.id,
        enabled: false,
        label: m.displayName,
      }))
    const dataBySite: PrebidModuleDataBySite<PrebidModuleType>[] = siteModulesConfig.map(({ enabled, config }) => {
      const { name, ...moduleData } = config
      return {
        code: config.name,
        moduleData,
        siteId: site.id,
        label: prebidModulesCatalog.find((module) => module.code === config.name)?.displayName || config.name,
        enabled,
      }
    })
    const allModules: PrebidModuleDataBySite<PrebidModuleType>[] = [...defaultData, ...dataBySite]

    setModules(allModules)
  }

  const updateSiteData = async (siteData: PrebidModuleDataBySite<PrebidModuleType>) => {
    try {
      const module = prebidModulesCatalog.find((m) => m.displayName === siteData.label)
      const moduleId = module?.id
      if (moduleId && module) {
        const update: PrebidModuleData<PrebidModuleType> = {
          name: module.code,
          ...siteData.moduleData,
        }
        if (module.type === 'RTD') {
          await rtdModuleUpdate.put(moduleId, site.id, update, siteData.enabled)
        }
        if (module.type === 'Identity') {
          await idModuleUpdate.put(moduleId, site.id, update, siteData.enabled)
        }
        if (module.type === 'Analytics') {
          await analyticsModuleUpdate.put(moduleId, site.id, update, siteData.enabled)
        }
        if (module.type === 'Rendering') {
          await putRenderingModule(moduleId, site.id, update, siteData.enabled)
        }
        toast.success(`${siteData.label} settings updated.`)
        setModules(modules.map((m) => (m.label === siteData.label ? { ...m, ...siteData } : m)))
      }
    } catch (_e) {
      toast.alert('An error has occurred, please retry later.')
    }
  }

  useEffect(() => {
    if (!site.id || isCatalogLoading || isModuleCatalogLoading) return

    // Legitimate data load when the site changes (async fetch then setState).
    // oxlint-disable-next-line react/set-state-in-effect
    getModulesData()
  }, [site, isCatalogLoading, isModuleCatalogLoading])

  return <PureModulesBySite isLoading={prebidModulesConfig.loading || isCatalogLoading || isModuleCatalogLoading} modules={modules} catalog={prebidModulesCatalog} updateSiteData={updateSiteData} />
}
